import { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { 
  CheckCircle2, XCircle, AlertCircle, Building2, FileText, 
  Calendar, DollarSign, Download, Phone, Star, Shield, 
  Users, Check, ArrowRight, Video, Server, ShieldCheck, Handshake,
  User, Briefcase, Info, CreditCard, ShoppingCart, Settings, Repeat, Sparkles, Loader2
} from "lucide-react"
import type { ModalidadeProposta, Proposta } from "@/types/comercial"
import { carregarPropostaPublica, cobrancaDoItem, responderPropostaPublica, totaisDosItens } from "@/lib/propostas"
import { decodePayload } from "@/utils/shareEncoder"
import { useToast } from "@/hooks/use-toast"

function fmtCurrency(v: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v)
}

function fmtDate(d: string) {
  if (!d) return "—"
  return new Date(d + "T00:00:00").toLocaleDateString("pt-BR")
}

function precoResumo(m: ModalidadeProposta) {
  const partes: string[] = []
  if (m.valorUnico > 0) partes.push(m.parcelas > 1 ? `${m.parcelas}x de ${fmtCurrency(m.valorUnico / m.parcelas)}` : fmtCurrency(m.valorUnico))
  if (m.valorMensal > 0) partes.push(`${fmtCurrency(m.valorMensal)}/mês`)
  return partes.join(" + ")
}

const MODALIDADE_VISUAL = {
  compra: { icon: ShoppingCart, chip: "bg-blue-50 text-blue-700" },
  locacao: { icon: Repeat, chip: "bg-violet-50 text-violet-700" },
  personalizada: { icon: Sparkles, chip: "bg-amber-50 text-amber-700" },
} as const

function EscolhaModalidades({
  modalidades,
  selecionada,
  escolhidaFinal,
  onSelect,
}: {
  modalidades: ModalidadeProposta[]
  selecionada: string | null
  escolhidaFinal?: string
  onSelect: (id: string) => void
}) {
  const travada = !!escolhidaFinal
  return (
    <section className="space-y-4" aria-labelledby="escolha-titulo">
      <div>
        <h2 id="escolha-titulo" className="text-xl font-bold text-slate-900 border-l-4 border-blue-600 pl-3">Escolha como contratar</h2>
        <p className="mt-1 pl-4 text-sm text-slate-500">Os mesmos itens acima, em {modalidades.length} formas de pagamento. Selecione a que faz mais sentido para você.</p>
      </div>
      <div className={`grid grid-cols-1 gap-4 ${modalidades.length === 2 ? "md:grid-cols-2" : modalidades.length >= 3 ? "md:grid-cols-3" : ""}`} role="radiogroup" aria-labelledby="escolha-titulo">
        {modalidades.map((m) => {
          const visual = MODALIDADE_VISUAL[m.tipo]
          const Icon = visual.icon
          const ativa = (escolhidaFinal ?? selecionada) === m.id
          const principalMensal = m.valorMensal > 0
          return (
            <button
              key={m.id}
              type="button"
              role="radio"
              aria-checked={ativa}
              disabled={travada}
              onClick={() => onSelect(m.id)}
              className={`relative flex flex-col rounded-2xl border-2 bg-white p-6 text-left transition-all print-card ${ativa ? "border-blue-600 shadow-[0_8px_30px_rgba(37,99,235,0.15)]" : "border-slate-200 hover:border-slate-300"} ${travada && !ativa ? "opacity-50" : ""}`}
            >
              {m.recomendada && (
                <span className="absolute -top-3 left-6 rounded-full bg-blue-600 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow">Recomendada</span>
              )}
              <div className="flex items-start justify-between gap-3">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${visual.chip}`}>
                  <Icon className="h-3.5 w-3.5" />
                  {m.tipo === "compra" ? "Compra" : m.tipo === "locacao" ? "Locação" : "Personalizada"}
                </span>
                <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 ${ativa ? "border-blue-600 bg-blue-600" : "border-slate-300"}`}>
                  {ativa && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                </span>
              </div>

              <h3 className="mt-4 text-lg font-bold text-slate-900">{m.nome}</h3>
              {m.descricao && <p className="mt-1 text-sm leading-relaxed text-slate-500">{m.descricao}</p>}

              <div className="mt-5 border-t border-slate-100 pt-5">
                {principalMensal ? (
                  <>
                    <p className="font-mono text-3xl font-black tracking-tight text-slate-900">
                      {fmtCurrency(m.valorMensal)}<span className="text-base font-bold text-slate-400">/mês</span>
                    </p>
                    <p className="mt-1 text-xs font-semibold text-slate-500">
                      {m.valorUnico > 0
                        ? `+ ${m.parcelas > 1 ? `${m.parcelas}x de ${fmtCurrency(m.valorUnico / m.parcelas)}` : fmtCurrency(m.valorUnico)} de adesão`
                        : "Sem taxa de adesão"}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-mono text-3xl font-black tracking-tight text-slate-900">
                      {m.parcelas > 1 ? <><span className="text-base font-bold text-slate-400">{m.parcelas}x </span>{fmtCurrency(m.valorUnico / m.parcelas)}</> : fmtCurrency(m.valorUnico)}
                    </p>
                    <p className="mt-1 text-xs font-semibold text-slate-500">
                      {m.parcelas > 1 ? `Total de ${fmtCurrency(m.valorUnico)} · ` : ""}Pagamento único, sem mensalidade
                    </p>
                  </>
                )}
                {m.fidelidadeMeses > 0 && (
                  <p className="mt-2 inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                    <Calendar className="h-3 w-3" />Contrato mínimo de {m.fidelidadeMeses} meses
                  </p>
                )}
              </div>

              {m.inclusos.length > 0 && (
                <ul className="mt-5 space-y-2">
                  {m.inclusos.map((inc) => (
                    <li key={inc} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" strokeWidth={2.5} />
                      {inc}
                    </li>
                  ))}
                </ul>
              )}
            </button>
          )
        })}
      </div>
      {travada && <p className="text-center text-xs font-semibold text-emerald-600">Opção confirmada no aceite da proposta.</p>}
    </section>
  )
}

type Acao = "aceitar" | "recusar" | "revisao" | null

const PropostaPublica = () => {
  const { token } = useParams<{ token: string }>()
  const { toast } = useToast()
  const [proposta, setProposta] = useState<Proposta | null>(null)
  const [loading, setLoading] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [acao, setAcao] = useState<Acao>(null)
  const [observacoes, setObservacoes] = useState("")
  const [confirmado, setConfirmado] = useState(false)
  const [erro, setErro] = useState(false)
  const [senhaDigitada, setSenhaDigitada] = useState("")
  const [senhaErro, setSenhaErro] = useState("")
  const [senhaValida, setSenhaValida] = useState(false)
  // Proposta do banco protegida: só chega depois que o servidor confere a senha
  const [senhaRequerida, setSenhaRequerida] = useState(false)
  const [senhaAcesso, setSenhaAcesso] = useState<string | undefined>()
  const [validandoSenha, setValidandoSenha] = useState(false)
  const [modalidadeSel, setModalidadeSel] = useState<string | null>(null)

  useEffect(() => {
    const mods = proposta?.modalidades ?? []
    if (mods.length && !modalidadeSel) {
      setModalidadeSel(proposta?.modalidadeEscolhida ?? mods.find((m) => m.recomendada)?.id ?? mods[0].id)
    }
  }, [proposta, modalidadeSel])

  useEffect(() => {
    if (proposta) {
      if (!proposta.senha) {
        setSenhaValida(true)
      }
    }
  }, [proposta])

  const handleValidarSenha = async (e: React.FormEvent) => {
    e.preventDefault()
    // Link legado com a proposta embutida na URL: a senha veio junto no payload
    if (proposta) {
      if (senhaDigitada === proposta.senha) {
        setSenhaValida(true)
        setSenhaAcesso(senhaDigitada)
        setSenhaErro("")
      } else {
        setSenhaErro("Senha incorreta. Tente novamente.")
      }
      return
    }
    if (!token || !senhaDigitada) return
    setValidandoSenha(true)
    try {
      const res = await carregarPropostaPublica(token, senhaDigitada)
      if (res?.acesso === "ok" && res.proposta) {
        setSenhaAcesso(senhaDigitada)
        setSenhaRequerida(false)
        setSenhaValida(true)
        setSenhaErro("")
        setProposta(res.proposta)
      } else {
        setSenhaErro("Senha incorreta. Tente novamente.")
      }
    } catch (err) {
      console.error("Erro ao validar senha da proposta:", err)
      setSenhaErro("Não foi possível validar a senha agora. Tente novamente.")
    } finally {
      setValidandoSenha(false)
    }
  }

  useEffect(() => {
    if (!token) { setErro(true); setLoading(false); return }

    // Link legado: a proposta inteira vem codificada na URL
    const p = decodePayload<Proposta>(token)
    if (p) {
      setProposta(p)
      setLoading(false)
      return
    }

    const fetchProposta = async () => {
      try {
        const res = await carregarPropostaPublica(token)
        if (!res) setErro(true)
        else if (res.acesso === "ok" && res.proposta) setProposta(res.proposta)
        else setSenhaRequerida(true)
      } catch (err) {
        console.error("Erro ao buscar proposta no banco:", err)
        setErro(true)
      } finally {
        setLoading(false)
      }
    }
    fetchProposta()
  }, [token])

  const handleConfirmar = async () => {
    if (!proposta || !acao) return
    if ((acao === "recusar" || acao === "revisao") && !observacoes.trim()) {
      toast({ title: "Campo obrigatório", description: "Por favor, explique sua solicitação antes de enviar.", variant: "destructive" })
      return
    }

    const mods = proposta.modalidades ?? []
    const escolhida = mods.find((m) => m.id === modalidadeSel)
    if (acao === "aceitar" && mods.length > 0 && !escolhida) {
      toast({ title: "Escolha uma opção", description: "Selecione como deseja contratar antes de aceitar.", variant: "destructive" })
      return
    }

    setSalvando(true)
    try {
      // O servidor grava status, aceite/recusa, modalidade e histórico
      const atualizada = await responderPropostaPublica(proposta.id, senhaAcesso, acao, {
        modalidadeId: escolhida?.id,
        motivo: observacoes.trim() || undefined,
      })
      if (atualizada) setProposta(atualizada)
      setConfirmado(true)
    } catch (err) {
      console.error("Erro ao registrar resposta da proposta:", err)
      const message = err instanceof Error ? err.message : (err as { message?: string })?.message
      toast({
        title: "Não foi possível registrar sua resposta",
        description: message || "Verifique sua conexão e tente novamente.",
        variant: "destructive",
      })
    } finally {
      setSalvando(false)
    }
  }

  const senhaModal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 max-w-md w-full text-center shadow-2xl relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl" />
        <ShieldCheck className="h-14 w-14 text-blue-500 mx-auto mb-4 animate-pulse" />

        <h1 className="text-2xl font-black text-white mb-2">Acesso Restrito</h1>
        <p className="text-slate-400 text-sm leading-relaxed mb-6">
          Esta proposta contém informações confidenciais (LGPD) e está protegida por senha. Por favor, insira a senha para visualizar.
        </p>

        <form onSubmit={handleValidarSenha} className="space-y-4">
          <div className="space-y-1.5 text-left">
            <Label htmlFor="senha_acesso" className="text-slate-350 text-xs font-bold uppercase tracking-wider pl-1 font-semibold text-slate-300">Senha de Acesso</Label>
            <Input
              id="senha_acesso"
              type="password"
              value={senhaDigitada}
              onChange={e => setSenhaDigitada(e.target.value)}
              placeholder="Digite a senha..."
              className="bg-slate-950 border-slate-800 text-white rounded-xl py-3 focus:border-blue-500 focus:ring-blue-500 placeholder-slate-600 text-center w-full"
              autoFocus
            />
          </div>

          {senhaErro && (
            <p className="text-red-400 text-xs font-semibold pl-1 text-center">
              {senhaErro}
            </p>
          )}

          <Button
            type="submit"
            disabled={validandoSenha}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 rounded-xl transition-all shadow-lg hover:shadow-blue-900/40"
          >
            {validandoSenha ? "Verificando..." : "Visualizar Proposta"}
          </Button>
        </form>
      </div>
    </div>
  )


  // Totais por tipo de cobrança (itens antigos são inferidos pela descrição)
  const { unica: totalAdesao, mensal: totalMensal } = totaisDosItens(proposta?.itens ?? [])
  const modalidades: ModalidadeProposta[] = proposta?.modalidades ?? []
  const temModalidades = modalidades.length > 0
  const modalidadeAtual = modalidades.find((m) => m.id === modalidadeSel) ?? null

  const pctDesconto = proposta && proposta.valorTotal > 0 ? Math.round((proposta.desconto / proposta.valorTotal) * 100) : 0
  const discountedAdesao = proposta ? totalAdesao - proposta.desconto : totalAdesao

  // ── Loading ──
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 rounded-full border-4 border-blue-500/20"></div>
            <div className="absolute inset-0 rounded-full border-4 border-t-blue-500 border-r-blue-500 animate-spin"></div>
          </div>
          <p className="text-blue-400 font-medium animate-pulse text-sm">Carregando Proposta Premium...</p>
        </div>
      </div>
    )
  }

  // ── Erro ──
  // ── Senha exigida pelo servidor (nenhum dado da proposta foi carregado) ──
  if (senhaRequerida && !proposta) {
    return <div className="min-h-screen bg-slate-950">{senhaModal}</div>
  }

  if (erro || !proposta) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 max-w-md w-full text-center shadow-2xl relative overflow-hidden">
          <div className="absolute -right-16 -top-16 w-32 h-32 bg-red-500/10 rounded-full blur-2xl" />
          <AlertCircle className="h-14 w-14 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-black text-white mb-2">Proposta não encontrada</h1>
          <p className="text-slate-400 text-sm leading-relaxed mb-6">
            O link de proposta que você acessou é inválido, expirou ou o payload está corrompido. Entre em contato com a equipe comercial da ItaCondo.
          </p>
          <a href="https://wa.me/5521998231720" target="_blank" rel="noopener noreferrer"
            className="w-full inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-xl transition-all shadow-lg hover:shadow-green-900/40">
            <Phone className="h-4 w-4" />
            Falar no WhatsApp
          </a>
        </div>
      </div>
    )
  }

  const getWhatsappLink = () => {
    // Use the short UUID-based URL instead of the full Base64 encoded payload
    const newPublicUrl = `${window.location.origin}/p/${proposta.id}`
    
    let text = ""
    if (proposta.status === "aceita") {
      text = `Olá! Aceitei a proposta *${proposta.titulo}* em nome de ${proposta.clienteNome}.\n\nVocê pode visualizar a confirmação no link:\n${newPublicUrl}`
    } else if (proposta.status === "recusada") {
      text = `Olá! A proposta *${proposta.titulo}* foi recusada.\n\nMotivo da Recusa: ${proposta.recusaMotivo}\n\nVocê pode ver os detalhes aqui:\n${newPublicUrl}`
    } else {
      // Revisão solicitada
      const lastEvent = proposta.historico[proposta.historico.length - 1];
      const desc = lastEvent ? lastEvent.descricao : "";
      text = `Olá! Gostaria de solicitar uma revisão na proposta *${proposta.titulo}*.\n\nObservações da revisão:\n${desc}\n\nLink para edição:\n${newPublicUrl}`
    }
    
    return `https://wa.me/5521998231720?text=${encodeURIComponent(text)}`
  }

  const jaDecidida = proposta.status === "aceita" || proposta.status === "recusada" || (confirmado && proposta.status === "rascunho")

  // ── Confirmado/Decidido ──
  if (confirmado || jaDecidida) {
    const isAceita = proposta.status === "aceita"
    const isRevisao = proposta.status === "rascunho"
    const showBlur = !senhaValida && proposta.senha
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
        <div className={`bg-slate-900 border border-slate-800 rounded-3xl p-8 max-w-md w-full text-center shadow-2xl relative overflow-hidden transition-all duration-500 ${showBlur ? 'filter blur-md pointer-events-none select-none' : ''}`}>
          <div className="absolute -left-16 -top-16 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl" />
          {isAceita ? (
            <>
              <CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto mb-4 animate-bounce" />
              <h1 className="text-2xl font-black text-white mb-2">Proposta Aceita!</h1>
              <p className="text-slate-300 text-sm leading-relaxed mb-4">
                Obrigado, <strong>{proposta.clienteNome}</strong>! Seu aceite eletrônico foi registrado com sucesso em nome de <strong>{proposta.clienteEmpresa || "sua empresa"}</strong>.
              </p>
              <p className="text-slate-500 text-xs mb-6">
                Para prosseguirmos com a elaboração e assinatura digital do contrato final, envie o comprovante atualizado clicando no botão abaixo.
              </p>
            </>
          ) : isRevisao ? (
            <>
              <Info className="h-16 w-16 text-amber-400 mx-auto mb-4 animate-pulse" />
              <h1 className="text-2xl font-black text-white mb-2">Revisão Solicitada</h1>
              <p className="text-slate-300 text-sm leading-relaxed mb-4">
                Sua solicitação de revisão para a proposta <strong>{proposta.titulo}</strong> foi salva no sistema.
              </p>
              <p className="text-slate-500 text-xs mb-6">
                Por favor, clique no botão abaixo para encaminhar a sua mensagem de revisão via WhatsApp à nossa equipe comercial.
              </p>
            </>
          ) : (
            <>
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4 animate-pulse" />
              <h1 className="text-2xl font-black text-white mb-2">Proposta Recusada</h1>
              <p className="text-slate-300 text-sm leading-relaxed mb-4">
                A proposta foi marcada como recusada no sistema. Agradecemos o feedback para nos ajudar a melhorar.
              </p>
              <p className="text-slate-500 text-xs mb-6">
                Envie o motivo da recusa para a nossa equipe pelo WhatsApp para podermos negociar novas condições.
              </p>
            </>
          )}
          
          <a href={getWhatsappLink()} target="_blank" rel="noopener noreferrer"
            className="w-full inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold py-3.5 rounded-xl transition-all shadow-lg hover:shadow-green-900/40">
            <Phone className="h-4 w-4" />
            Enviar no WhatsApp da ItaCondo
          </a>
        </div>

        {/* Modal de Senha */}
        {showBlur && senhaModal}
      </div>
    )
  }

  // Helper para renderizar ícones na tabela de itens
  const getItemIcon = (item: { descricao: string; cobranca?: "unica" | "mensal"; quantidade: number; valorUnitario: number; valorTotal: number; id: string }) => {
    if (cobrancaDoItem(item) === "mensal") {
      return <Calendar className="h-4 w-4 text-purple-500 shrink-0 mt-0.5" />
    }
    const d = item.descricao.toLowerCase()
    if (d.includes("instalação") || d.includes("instalacao") || d.includes("configuração") || d.includes("configuracao")) {
      return <Settings className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
    }
    if (d.includes("mensal") || d.includes("mensalidade") || d.includes("recorrente") || d.includes("assinatura") || d.includes("licença")) {
      return <Calendar className="h-4 w-4 text-purple-500 shrink-0 mt-0.5" />
    }
    return <ShoppingCart className="h-4 w-4 text-indigo-500 shrink-0 mt-0.5" />
  }

  const showBlur = !senhaValida && proposta?.senha

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 pb-28 font-sans relative">
      
      {/* Estilos customizados para impressão */}
      <style>{`
        @media print {
          body { background: white; color: black; padding-bottom: 0; }
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          .print-card { border: 1px solid #ccc !important; box-shadow: none !important; }
        }
      `}</style>

      <div className={`transition-all duration-500 ${showBlur ? 'filter blur-md pointer-events-none select-none' : ''}`}>

      {/* ── Top Curved Premium Header ── */}
      <div className="relative bg-white overflow-hidden border-b no-print">
        {/* Curved Wave background on large screens */}
        <div className="absolute right-0 top-0 bottom-0 w-full md:w-1/2 bg-gradient-to-tr from-blue-700 via-indigo-900 to-slate-950 text-white clip-wave hidden md:block">
          <style>{`
            .clip-wave {
              clip-path: polygon(25% 0%, 100% 0%, 100% 100%, 0% 100%);
            }
          `}</style>
          
          <div className="h-full flex flex-col items-center justify-center p-12 pl-36 relative">
            <img src="/logo_sfundo.png" alt="ItaCondo" className="h-12 w-auto mb-10 object-contain drop-shadow" />
            
            {/* Glowing Bot Avatar (Cyberpunk theme) */}
            <div className="relative w-48 h-48 mx-auto flex items-center justify-center">
              <div className="absolute inset-0 bg-blue-500/20 rounded-full animate-ping pointer-events-none" />
              <div className="absolute -inset-2 bg-gradient-to-tr from-blue-400 to-indigo-500 rounded-full blur opacity-40 animate-pulse" />
              <div className="w-44 h-44 rounded-full bg-slate-900 border-4 border-blue-400/80 flex flex-col items-center justify-center shadow-2xl relative overflow-hidden">
                <div className="w-24 h-20 bg-white rounded-[32px] border-4 border-blue-600 flex flex-col items-center justify-center relative shadow-inner">
                  <div className="flex gap-4 mb-2">
                    <div className="w-3.5 h-3.5 bg-blue-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(59,130,246,0.8)]" />
                    <div className="w-3.5 h-3.5 bg-blue-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(59,130,246,0.8)]" />
                  </div>
                  <div className="w-8 h-2 border-b-4 border-blue-600 rounded-b-full" />
                  <div className="absolute -left-3 top-6 w-3 h-8 bg-blue-600 rounded-l-lg" />
                  <div className="absolute -right-3 top-6 w-3 h-8 bg-blue-600 rounded-r-lg" />
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 flex flex-col items-center">
                    <div className="w-1.5 h-6 bg-blue-600" />
                    <div className="w-4 h-4 bg-blue-500 rounded-full animate-bounce shadow-[0_0_10px_rgba(59,130,246,1)]" />
                  </div>
                </div>
              </div>
            </div>
            
            <p className="text-xs text-blue-300 mt-6 tracking-widest font-mono uppercase">IA & SEGURANÇA CONDOMINIAL</p>
          </div>
        </div>

        {/* Header container for standard mobile layout */}
        <div className="max-w-6xl mx-auto px-6 py-12 md:py-16 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-full">
              <Building2 className="h-4 w-4 text-blue-600" />
              <span className="font-bold text-blue-900 tracking-wide text-xs">itaCondo Sistemas</span>
            </div>
            
            <div className="relative">
              <h1 className="text-5xl font-black tracking-tight text-slate-900 pb-2">
                Orçamento
              </h1>
              <div className="h-1.5 w-20 bg-blue-600 rounded-full absolute bottom-0 left-0" />
            </div>

            <p className="text-slate-600 text-base leading-relaxed max-w-md font-medium">
              {proposta.escopo ? proposta.escopo.split('.')[0] + '.' : "Solução personalizada de tecnologia e segurança para o seu condomínio."}
            </p>

            {/* Info Cards */}
            <div className="bg-slate-50 border rounded-2xl p-5 space-y-3.5 max-w-md shadow-sm">
              <div className="flex items-center gap-3.5">
                <div className="p-2 rounded-xl bg-blue-100 text-blue-700">
                  <User className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Cliente</p>
                  <p className="text-sm font-bold text-slate-800">{proposta.clienteNome}</p>
                </div>
              </div>
              <div className="flex items-center gap-3.5 border-t pt-3">
                <div className="p-2 rounded-xl bg-blue-100 text-blue-700">
                  <Briefcase className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Empresa/Projeto</p>
                  <p className="text-sm font-bold text-slate-800">{proposta.clienteEmpresa || "Curso C&C"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3.5 border-t pt-3">
                <div className="p-2 rounded-xl bg-blue-100 text-blue-700">
                  <FileText className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Proposta</p>
                  <p className="text-sm font-bold text-slate-800">{proposta.titulo}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile view only - top elements stack */}
          <div className="block md:hidden bg-gradient-to-tr from-blue-700 via-indigo-900 to-slate-950 text-white rounded-3xl p-6 text-center space-y-6">
            <img src="/logo_sfundo.png" alt="ItaCondo" className="h-10 w-auto mx-auto object-contain" />
            <div className="relative w-36 h-36 mx-auto flex items-center justify-center">
              <div className="w-32 h-32 rounded-full bg-slate-900 border-4 border-blue-400 flex flex-col items-center justify-center shadow-lg relative overflow-hidden">
                <div className="w-16 h-14 bg-white rounded-2xl border-2 border-blue-600 flex flex-col items-center justify-center relative shadow-inner">
                  <div className="flex gap-2.5 mb-1.5">
                    <div className="w-2.5 h-2.5 bg-blue-500 rounded-full" />
                    <div className="w-2.5 h-2.5 bg-blue-500 rounded-full" />
                  </div>
                  <div className="w-6 h-1 border-b-2 border-blue-600 rounded-b-full" />
                </div>
              </div>
            </div>
            <p className="text-xs text-blue-300 font-bold">Tecnologia que Conecta</p>
          </div>
        </div>
      </div>

      {/* Printable Title Block */}
      <div className="hidden print:block max-w-4xl mx-auto px-6 pt-10 text-center space-y-4">
        <img src="/logo_sfundo.png" alt="ItaCondo" className="h-14 w-auto mx-auto" />
        <h1 className="text-3xl font-bold uppercase tracking-wide">Proposta Comercial / Orçamento</h1>
        <p className="text-lg font-bold">{proposta.titulo}</p>
        <p className="text-slate-500 text-sm">Cliente: {proposta.clienteNome} | Empresa: {proposta.clienteEmpresa || "-"}</p>
        <div className="border-t pt-4"></div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 mt-8 space-y-8">
        
        {/* Date / Validity Meta block */}
        <div className="bg-white rounded-2xl border p-5 shadow-sm grid grid-cols-1 sm:grid-cols-2 gap-4 print-card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-amber-50 text-amber-600">
              <Calendar className="h-6 w-6" />
            </div>
            <div>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Válida até</p>
              <p className="font-bold text-slate-800 text-sm">{fmtDate(proposta.validadeAte)}</p>
              <p className="text-[10px] text-slate-400">Sujeito a reajustes após a data</p>
            </div>
          </div>
          <div className="flex items-center gap-3 border-t sm:border-t-0 sm:border-l pt-3 sm:pt-0 sm:pl-6">
            <div className="p-3 rounded-xl bg-blue-50 text-blue-600">
              <CreditCard className="h-6 w-6" />
            </div>
            <div>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{temModalidades ? "Formas de contratação" : "Investimento Estimado"}</p>
              {temModalidades ? (
                <p className="font-bold text-slate-800 text-sm">{modalidades.map((m) => m.nome).join(" ou ")}</p>
              ) : (
              <p className="font-bold text-slate-800 text-sm">
                {totalAdesao > 0 ? (
                  proposta.desconto > 0 ? (
                    <>
                      <span className="text-slate-450 line-through mr-1 font-normal">{fmtCurrency(totalAdesao)}</span>
                      <span className="text-emerald-600 font-extrabold">{fmtCurrency(discountedAdesao)} Único</span>
                    </>
                  ) : (
                    `${fmtCurrency(totalAdesao)} Único`
                  )
                ) : ''} 
                {totalAdesao > 0 && totalMensal > 0 ? ' + ' : ''}
                {totalMensal > 0 ? `${fmtCurrency(totalMensal)}/mês` : ''}
              </p>
              )}
              <p className="text-[10px] text-slate-400">{temModalidades ? "Compare as opções e escolha abaixo" : "Condições de parcelamento detalhadas abaixo"}</p>
            </div>
          </div>
        </div>

        {/* ITENS DA PROPOSTA (DETALHAMENTO) */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900 border-l-4 border-blue-600 pl-3">Detalhamento dos Serviços</h2>
            <Button 
              onClick={() => window.print()}
              variant="outline" 
              size="sm"
              className="border-slate-200 hover:bg-slate-50 text-slate-700 gap-2 no-print"
            >
              <Download className="h-4 w-4" />
              Imprimir / PDF
            </Button>
          </div>
          
          <div className="bg-white rounded-2xl border overflow-hidden shadow-sm print-card">
            {/* Dark Navy Table Header matches the budget example */}
            <div className="bg-[#0B1530] px-6 py-4 flex items-center justify-between">
              <h3 className="font-bold text-white text-sm tracking-wide">ITENS DA PROPOSTA COMERCIAL</h3>
              <Badge className="bg-blue-600 text-white border-0 text-xs">Vigente</Badge>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs font-bold text-slate-400 uppercase tracking-wider bg-slate-100/50">
                    <th className="p-4 pl-6">Descrição</th>
                    <th className="p-4 text-center">Quant</th>
                    <th className="p-4 text-right pr-6">Valor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {proposta.itens.map((item, idx) => (
                    <tr key={item.id} className="hover:bg-slate-50/50 even:bg-slate-50/20">
                      <td className="p-4 pl-6 flex items-start gap-3">
                        {getItemIcon(item)}
                        <span className="font-semibold text-slate-800 leading-normal">{item.descricao}</span>
                      </td>
                      <td className="p-4 text-center text-slate-600 font-bold font-mono">
                        {String(item.quantidade).padStart(2, '0')}
                      </td>
                      <td className="p-4 text-right font-extrabold text-blue-600 pr-6 font-mono text-base">
                        {cobrancaDoItem(item) === "mensal" ? `${fmtCurrency(item.valorTotal)}/mês` : fmtCurrency(item.valorTotal)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {temModalidades ? (
            <EscolhaModalidades
              modalidades={modalidades}
              selecionada={modalidadeSel}
              escolhidaFinal={proposta.modalidadeEscolhida}
              onSelect={setModalidadeSel}
            />
          ) : (
          <>
          <div className={`grid grid-cols-1 gap-4 ${totalMensal > 0 ? "md:grid-cols-2" : ""}`}>
            <div className="bg-gradient-to-r from-blue-900 to-indigo-950 rounded-2xl p-6 text-white flex items-center gap-4 relative overflow-hidden shadow-md">
              <div className="absolute right-0 bottom-0 opacity-10 pointer-events-none translate-x-4 translate-y-4">
                <Settings className="w-32 h-32" />
              </div>
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-xl font-bold shrink-0">1</div>
              <div>
                <p className="text-[10px] text-blue-300 font-bold uppercase tracking-wider">Adesão / Implantação</p>
                <p className="text-xl font-black">{fmtCurrency(totalAdesao)}</p>
                <p className="text-[10px] text-blue-400 mt-1">Configuração inicial de infraestrutura & hardware</p>
              </div>
            </div>
            
            {totalMensal > 0 && (
            <div className="bg-gradient-to-r from-indigo-900 to-purple-950 rounded-2xl p-6 text-white flex items-center gap-4 relative overflow-hidden shadow-md">
              <div className="absolute right-0 bottom-0 opacity-10 pointer-events-none translate-x-4 translate-y-4">
                <Calendar className="w-32 h-32" />
              </div>
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-xl font-bold shrink-0">2</div>
              <div>
                <p className="text-[10px] text-purple-300 font-bold uppercase tracking-wider">Serviços Mensais</p>
                <p className="text-xl font-black text-blue-300">{fmtCurrency(totalMensal)}/mês</p>
                <p className="text-[10px] text-purple-400 mt-1">Suporte continuado & licenciamento de software</p>
              </div>
            </div>
            )}
          </div>

          {/* Totals & Discounts Card */}
          <div className="bg-white rounded-2xl border p-6 shadow-sm space-y-4 print-card">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-4 border-b">
              <div>
                <p className="text-sm font-bold text-slate-700">Resumo de Faturamento:</p>
                <p className="text-xs text-slate-400 mt-0.5">Emissão de notas gerada diretamente contra o CNPJ do condomínio.</p>
              </div>
              <div className="space-y-1 font-semibold text-sm text-slate-600 text-right w-full md:w-auto">
                <div className="flex justify-between md:justify-end gap-6">
                  <span>Subtotal Bruto:</span>
                  <span className="font-bold text-slate-800 font-mono">{fmtCurrency(proposta.valorTotal)}</span>
                </div>
                {proposta.desconto > 0 && (
                  <div className="flex justify-between md:justify-end gap-6 text-emerald-600">
                    <span>Desconto Aplicado ({pctDesconto}%):</span>
                    <span className="font-bold font-mono">-{fmtCurrency(proposta.desconto)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-2">
              <div className="text-center sm:text-left">
                {proposta.desconto > 0 ? (
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50 px-2.5 py-1 text-xs">
                    Economia de {fmtCurrency(proposta.desconto)} ({pctDesconto}%) garantida!
                  </Badge>
                ) : (
                  <span className="text-xs text-slate-400">Condições válidas pelo prazo de vigência.</span>
                )}
              </div>
              
              <div className="text-center sm:text-right">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold">Investimento Líquido Final</p>
                <p className="text-3xl font-black text-slate-900 font-mono">{fmtCurrency(proposta.valorFinal)}</p>
              </div>
            </div>
          </div>
          </>
          )}
        </div>

        {/* Condições de parcelamento / Observações */}
        {proposta.observacoes && (
          <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-6 shadow-sm flex items-start gap-4 print-card">
            <div className="p-2 rounded-xl bg-blue-100 text-blue-700 shrink-0">
              <Info className="h-5 w-5" />
            </div>
            <div>
              <h4 className="font-bold text-blue-900 text-sm mb-1.5">Informações de Pagamento & Observações</h4>
              <p className="text-slate-700 text-sm whitespace-pre-line leading-relaxed">{proposta.observacoes}</p>
            </div>
          </div>
        )}

        {/* Termos e Garantias */}
        {proposta.termos && (
          <div className="bg-slate-100 rounded-2xl border p-6 print-card">
            <h3 className="font-bold text-slate-700 text-xs uppercase tracking-wider mb-2">Termos Contratuais & Garantias</h3>
            <p className="text-slate-500 text-xs whitespace-pre-line leading-relaxed">{proposta.termos}</p>
          </div>
        )}

        {/* Metodologia ESI */}
        <div className="bg-white rounded-2xl border p-8 shadow-sm print-card space-y-6 print-break no-print">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-slate-900">Metodologia ESI</h2>
            <p className="text-slate-400 text-sm mt-0.5 font-medium">Entendimento, Solução e Implantação de Alta Performance</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
            <div className="space-y-2 border rounded-2xl p-5 hover:border-blue-400 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 font-bold flex items-center justify-center text-lg">E</div>
              <h4 className="font-bold text-slate-900 text-sm">Entendimento</h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                Estudamos a planta do seu condomínio, avaliando pontos cegos de segurança, fluxo de moradores e necessidades de automação comercial e relatórios.
              </p>
            </div>
            <div className="space-y-2 border rounded-2xl p-5 hover:border-blue-400 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 font-bold flex items-center justify-center text-lg">S</div>
              <h4 className="font-bold text-slate-900 text-sm">Solução</h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                Projetamos a combinação ideal de equipamentos físicos (Câmeras Dome, cabos Gigabit) e módulos de software (Encomendas, Folha de Ponto por IA, Visitantes).
              </p>
            </div>
            <div className="space-y-2 border rounded-2xl p-5 hover:border-blue-400 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 font-bold flex items-center justify-center text-lg">I</div>
              <h4 className="font-bold text-slate-900 text-sm">Implantação</h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                Nossa equipe de técnicos monta a infraestrutura, testa as câmeras e ensina os porteiros e a administração a usar o sistema de controle com excelência.
              </p>
            </div>
          </div>
        </div>

        {/* ── Decision Section with 3 Action options ── */}
        <div id="aprovar-secao" className="bg-white rounded-3xl border shadow-md p-8 text-center space-y-6 print-card no-print">
          {!acao ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-black text-slate-900">Decisão do Orçamento</h2>
              <p className="text-slate-500 text-sm max-w-lg mx-auto leading-relaxed">
                Ao selecionar uma opção, o sistema registrará a sua ação de forma eletrônica. Nossa equipe será notificada de imediato.
              </p>
              
              {/* Accept / Request Revision / Refuse Button Grid */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-xl mx-auto pt-2">
                <Button
                  onClick={() => setAcao("aceitar")}
                  className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white flex-1 py-6 rounded-xl font-bold text-sm shadow-md hover:shadow-emerald-900/10 transition-all"
                >
                  <CheckCircle2 className="h-5 w-5" />
                  Aceitar Proposta
                </Button>
                
                <Button
                  onClick={() => setAcao("revisao")}
                  variant="outline"
                  className="gap-2 text-amber-600 border-amber-200 hover:bg-amber-50 hover:text-amber-700 flex-1 py-6 rounded-xl font-bold text-sm"
                >
                  <AlertCircle className="h-5 w-5" />
                  Solicitar Revisão
                </Button>
                
                <Button
                  onClick={() => setAcao("recusar")}
                  variant="outline"
                  className="gap-2 text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600 flex-1 py-6 rounded-xl font-bold text-sm"
                >
                  <XCircle className="h-5 w-5" />
                  Recusar Proposta
                </Button>
              </div>
            </div>
          ) : acao === "aceitar" ? (
            <div className="space-y-4 text-center max-w-md mx-auto animate-fadeIn">
              <div className="p-3 bg-emerald-50 rounded-full w-14 h-14 flex items-center justify-center mx-auto text-emerald-600">
                <CheckCircle2 className="h-8 w-8 animate-bounce" />
              </div>
              <div>
                <p className="font-bold text-slate-900 text-lg">Confirmar aceite da proposta?</p>
                <p className="text-slate-500 text-xs mt-1 leading-relaxed">
                  Ao clicar em Confirmar, você formaliza eletronicamente o aceite desta proposta comercial em nome de <strong>{proposta.clienteEmpresa || proposta.clienteNome}</strong>.
                </p>
                {modalidadeAtual && (
                  <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-left">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-blue-500">Opção escolhida</p>
                    <p className="text-sm font-bold text-slate-900">{modalidadeAtual.nome}</p>
                    <p className="text-xs text-slate-600">{precoResumo(modalidadeAtual)}</p>
                  </div>
                )}
                {temModalidades && !modalidadeAtual && (
                  <p className="mt-3 text-xs font-semibold text-red-500">Selecione uma das opções de contratação acima.</p>
                )}
              </div>
              <div className="flex gap-3 justify-center pt-2">
                <Button variant="outline" className="rounded-xl px-5" onClick={() => setAcao(null)} disabled={salvando}>Voltar</Button>
                <Button
                  onClick={handleConfirmar}
                  disabled={salvando}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-6 font-semibold gap-2 shadow-lg"
                >
                  {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {salvando ? "Registrando..." : "Confirmar e Assinar"}
                </Button>
              </div>
            </div>
          ) : acao === "revisao" ? (
            <div className="space-y-4 max-w-md mx-auto text-center animate-fadeIn">
              <div className="p-3 bg-amber-50 rounded-full w-14 h-14 flex items-center justify-center mx-auto text-amber-500">
                <AlertCircle className="h-8 w-8 animate-pulse" />
              </div>
              <div>
                <p className="font-bold text-slate-900 text-lg">Solicitar Revisão do Orçamento</p>
                <p className="text-slate-500 text-xs mt-1">Indique o que você deseja alterar ou negociar nesta proposta (valores, prazos, itens).</p>
              </div>
              <Textarea
                value={observacoes}
                onChange={e => setObservacoes(e.target.value)}
                placeholder="Exemplo: Preciso parcelar em mais vezes ou trocar a quantidade de câmeras..."
                className="min-h-[100px] rounded-xl text-sm border-slate-200 focus:border-amber-400 focus:ring-amber-400"
              />
              <div className="flex gap-3 justify-center pt-2">
                <Button variant="outline" className="rounded-xl px-5" onClick={() => setAcao(null)} disabled={salvando}>Voltar</Button>
                <Button 
                  onClick={handleConfirmar} 
                  disabled={!observacoes.trim() || salvando}
                  className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl px-6 font-semibold gap-2"
                >
                  {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {salvando ? "Enviando..." : "Solicitar Revisão"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 max-w-md mx-auto text-center animate-fadeIn">
              <div className="p-3 bg-red-50 rounded-full w-14 h-14 flex items-center justify-center mx-auto text-red-500">
                <XCircle className="h-8 w-8 animate-pulse" />
              </div>
              <div>
                <p className="font-bold text-slate-900 text-lg">Confirmar Recusa da Proposta</p>
                <p className="text-slate-500 text-xs mt-1">Sua resposta nos ajuda a entender melhor e a renegociar outras opções.</p>
              </div>
              <Textarea
                value={observacoes}
                onChange={e => setObservacoes(e.target.value)}
                placeholder="Por favor, explique o motivo da recusa (preço, concorrência, etc)..."
                className="min-h-[100px] rounded-xl text-sm border-slate-200 focus:border-red-400 focus:ring-red-400"
              />
              <div className="flex gap-3 justify-center pt-2">
                <Button variant="outline" className="rounded-xl px-5" onClick={() => setAcao(null)} disabled={salvando}>Voltar</Button>
                <Button 
                  onClick={handleConfirmar} 
                  disabled={!observacoes.trim() || salvando}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-xl px-6 font-semibold gap-2"
                >
                  {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {salvando ? "Enviando..." : "Enviar Recusa"}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* ── Solid Navy Footer matching the budget layout image ── */}
        <div className="bg-[#0B1530] text-slate-300 py-8 px-6 sm:px-10 rounded-3xl mt-8">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-col items-center md:items-start gap-2 text-center md:text-left">
              <img src="/logo_sfundo.png" alt="ItaCondo" className="h-8 w-auto object-contain" />
              <p className="text-xs text-slate-400 mt-1">Tecnologia que conecta pessoas e resultados.</p>
            </div>
            
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-blue-400" />
              <a href="https://www.itacondo.com.br" target="_blank" rel="noopener noreferrer" className="text-xs hover:underline hover:text-white transition-colors">
                www.itacondo.com.br
              </a>
            </div>
            
            <div className="flex flex-col items-center md:items-end gap-1.5 text-center md:text-right">
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Fale Conosco</p>
              <a href="https://wa.me/5521998231720" target="_blank" rel="noopener noreferrer" 
                className="inline-flex items-center gap-2 bg-blue-600/35 hover:bg-blue-600/50 text-white text-xs font-bold px-4 py-2 rounded-xl transition-all border border-blue-500/20">
                <Phone className="h-3.5 w-3.5 text-green-400 animate-pulse" />
                Falar com Consultor
              </a>
            </div>
          </div>
        </div>

      </div>

      {/* Sticky Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t py-4 px-6 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] z-50 flex items-center justify-between no-print">
        <div className="max-w-4xl mx-auto w-full flex flex-row items-center justify-between gap-4">
          {modalidadeAtual ? (
            <div className="min-w-0">
              <p className="text-[9px] text-slate-400 uppercase font-bold tracking-wider">Opção selecionada</p>
              <p className="truncate text-sm font-extrabold text-slate-900">
                {modalidadeAtual.nome}
                <span className="ml-2 font-mono font-bold text-blue-600">{precoResumo(modalidadeAtual)}</span>
              </p>
            </div>
          ) : (
          <div className="flex items-center gap-6">
            <div>
              <p className="text-[9px] text-slate-400 uppercase font-bold tracking-wider">Adesão Única</p>
              <p className="text-base font-extrabold text-slate-900 font-mono">
                {proposta.desconto > 0 ? (
                  <>
                    <span className="text-xs text-slate-400 line-through mr-1.5 font-normal">{fmtCurrency(totalAdesao)}</span>
                    <span className="text-emerald-600 font-extrabold">{fmtCurrency(discountedAdesao)}</span>
                  </>
                ) : (
                  fmtCurrency(totalAdesao)
                )}
              </p>
            </div>
            {totalMensal > 0 && (
            <div className="border-l pl-6">
              <p className="text-[9px] text-slate-400 uppercase font-bold tracking-wider">Valor Mensal</p>
              <p className="text-base font-extrabold text-blue-600 font-mono">{fmtCurrency(totalMensal)}/mês</p>
            </div>
            )}
          </div>
          )}
          
          <div className="flex gap-3">
            <a 
              href="https://wa.me/5521998231720" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold px-4 py-2 rounded-xl transition-all"
            >
              <Phone className="h-3.5 w-3.5 text-green-500" />
              Dúvidas
            </a>
            <Button 
              onClick={() => {
                const el = document.getElementById('aprovar-secao')
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth' })
                } else {
                  window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
                }
              }}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-6 py-2.5 rounded-xl shadow transition-all hover:scale-105"
            >
              Decidir
            </Button>
          </div>
        </div>
      </div>
      </div>
      
      {/* Modal de Senha */}
      {showBlur && senhaModal}
    </div>
  )
}

export default PropostaPublica
